import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "partxplorer_search_history";
const MAX_HISTORY = 10;

export interface SearchHistoryItem {
  query: string;
  type: "oem" | "ref";
  timestamp: number;
}

export const useSearchHistory = () => {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setHistory(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse search history", e);
      }
    }
  }, []);

  const addToHistory = useCallback((query: string, type: "oem" | "ref") => {
    const newItem: SearchHistoryItem = {
      query,
      type,
      timestamp: Date.now(),
    };

    setHistory((prev) => {
      // Remove duplicates
      const filtered = prev.filter((item) => item.query !== query);
      const updated = [newItem, ...filtered].slice(0, MAX_HISTORY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const removeItem = useCallback((query: string) => {
    setHistory((prev) => {
      const updated = prev.filter((item) => item.query !== query);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const getHistoryByType = useCallback((type: "oem" | "ref") => {
    return history.filter(item => item.type === type);
  }, [history]);

  return {
    history,
    addToHistory,
    clearHistory,
    removeItem,
    getHistoryByType,
  };
};
